import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useApiDocs, hmacSha256 } from '../../../components/apidocs';

export type WebhookToolTab = 'verifier' | 'builder' | 'codegen';

/** สถานะ + ตรรกะของแท็บ Webhook Toolbox (ทำงานในเบราว์เซอร์ล้วน) */
export const useWebhookToolbox = () => {
  const { copyCode } = useApiDocs();

  const [tab, setTab] = useState<WebhookToolTab>('verifier');
  // Signature Verifier
  const [verifyPayload, setVerifyPayload] = useState('');
  const [verifySignature, setVerifySignature] = useState('');
  const [verifySecret, setVerifySecret] = useState('');
  const [verifyResult, setVerifyResult] = useState<null | { valid: boolean; expected: string }>(null);
  // Payload Builder
  const [builderEvent, setBuilderEvent] = useState('order.paid');
  // Code Generator
  const [codeLang, setCodeLang] = useState('javascript');

  const verify = useCallback(async () => {
    if (!verifySecret || !verifyPayload) {
      setVerifyResult(null);
      toast.error('กรุณากรอก Secret และ Payload');
      return;
    }
    try {
      const expected = await hmacSha256(verifySecret, verifyPayload);
      const valid = expected === verifySignature.trim().toLowerCase();
      setVerifyResult({ valid, expected });
    } catch {
      toast.error('เกิดข้อผิดพลาดในการคำนวณ');
    }
  }, [verifySecret, verifyPayload, verifySignature]);

  /** คัดลอกคำสั่ง cURL ที่เซ็น signature ให้เรียบร้อย (ใช้ secret จากแท็บ Verifier) */
  const copyCurlWithSignature = useCallback(async (payload: Record<string, unknown>) => {
    if (!verifySecret) {
      toast.info('กรอก Secret ที่แท็บ Signature Verifier ก่อน จะ generate signature ให้');
      return;
    }
    const sig = await hmacSha256(verifySecret, JSON.stringify(payload));
    const curlCmd = `curl -X POST "https://your-server.com/webhook" \\\n  -H "Content-Type: application/json" \\\n  -H "X-Webhook-Signature: ${sig}" \\\n  -d '${JSON.stringify(payload)}'`;
    copyCode(curlCmd, `wh-curl-${builderEvent}`);
    toast.success('คัดลอก cURL + Signature แล้ว!');
  }, [verifySecret, builderEvent, copyCode]);

  return {
    tab,
    setTab,
    verifyPayload,
    setVerifyPayload,
    verifySignature,
    setVerifySignature,
    verifySecret,
    setVerifySecret,
    verifyResult,
    verify,
    builderEvent,
    setBuilderEvent,
    codeLang,
    setCodeLang,
    copyCurlWithSignature,
  };
};

export type WebhookToolboxController = ReturnType<typeof useWebhookToolbox>;
